import React, { useState, useEffect, useContext, memo } from 'react'
import ReactDOM from 'react-dom'
import { Image, AddToTrolley } from '@sainsburys-tech/bolt'
import { isEmpty } from '@sainsburys-tech/boltui-utils'
import { ThemeContext } from 'styled-components'
import {
  StyledHeadingContainer,
  StyledInstagramIcon,
  StyledImageContainer,
  StyledAuthorHandle,
  StyledViewProductsIcon,
  StyledTrolleyIcon,
  StyledViewProductsLabel,
  Carousel,
  SliderWrapper,
  StyledButton,
  StyledCTALink
} from './C002.styles'
import { fetcher } from '../../../helpers/ComponentHelper'
import { formatImages, createFilters, formatImagesStorystream } from '../../functional/UGC/ugc.helpers.js'
import { onPDPUGCLoaded, onQuickViewSlidingPanelShut } from '../../functional/UGC/ugc.tracking'
import { C002Props, ImageDataType } from './types'
import C002Drawer from './C002Drawer'
import C002StorystreamDrawer from './C002StorystreamDrawer'

/**
 * C002 is a Carousel component that is to be used on PDP pages to show user generated content. It can be authored with an heading and bodyCopy the rest of the data comes from the curalate API.
 */

const C002 = ({ heading, bodyCopy, productIdentifier, tags, streamID }: C002Props): React.ReactElement => {
  const [UGCStatus, setStatus] = useState({
    isError: false,
    isLoading: true,
    curalateCalled: false
  })
  const [imageData, setImageData] = useState<ImageDataType[]>([])
  const [selectedImage, setSelectedImage] = useState<ImageDataType>(null)

  const theme = useContext(ThemeContext)
  const { channel } = theme
  const isTags = tags?.length > 0
  const tagsString = isTags ? tags.toString() : ''
  const isSainsburys = channel === 'sainsburys'
  const limit = isSainsburys ? 30 : 12
  const DrawerVariant = isSainsburys ? C002StorystreamDrawer : C002Drawer

  const filterImageData = (formattedImages, matcher) => {
    return [...imageData, ...formattedImages].filter(
      (image, index, self) => index === self.findIndex((img) => img[matcher] === image[matcher])
    )
  }

  const fetchUGCdata = async (hasTags: boolean) => {
    let apiUrl = ''

    if (isSainsburys) {
      apiUrl = `https://d29qb9vav0xwuc.cloudfront.net/v4/sainsburys-pilot/${streamID}/search/live.json?access_token=54da843033ef26e29397dce44333b126def2035e&rpp=${limit}`
    } else {
      const filter = hasTags ? `&filter=${createFilters(tagsString)}` : `&filter=productId:${productIdentifier}`
      apiUrl = `/global-api/ugc-curalate?limit=${limit}${filter}`
    }

    try {
      const { json, ok, status } = await fetcher(apiUrl)
      if (!ok) {
        throw new Error(`There has been an error with (${status})`)
      }
      const filteredImageData = isSainsburys
        ? filterImageData(formatImagesStorystream(json.blocks), 'description')
        : filterImageData(formatImages(json.data.items), 'id')
      return ReactDOM.unstable_batchedUpdates(() => {
        setImageData(filteredImageData)
        setStatus({
          isError: false,
          isLoading: false,
          curalateCalled: true
        })
        if (!hasTags) onPDPUGCLoaded(productIdentifier, window)
      })
    } catch (err) {
      console.error(err)
      return setStatus({
        isError: true,
        isLoading: false,
        curalateCalled: true
      })
    }
  }

  useEffect(() => {
    fetchUGCdata(false)
  }, [])

  useEffect(() => {
    if (!isSainsburys && isTags && UGCStatus.curalateCalled && imageData.length < 12) {
      fetchUGCdata(true)
    }
  }, [tags, UGCStatus.curalateCalled])

  if ((!UGCStatus.isLoading && isEmpty(imageData)) || UGCStatus.isError) return null

  const createImageItem = (item: ImageDataType, index: number) => {
    const numberOfProducts = item.productIds?.length
    const WrapperImage = isSainsburys ? 'div' : StyledButton
    return (
      <StyledImageContainer
        data-test={`c002-image-container-${index}`}
        key={`ugc-img-${index}`}
        role='listitem'
        isSains={isSainsburys}
      >
        <WrapperImage onClick={() => setSelectedImage({ ...item, index })}>
          <Image
            data-test={`c002-image-${index}`}
            src={item.url}
            alt={
              isSainsburys ? `Image by @${item?.author}` : `Image by @${item?.author} with ${numberOfProducts} products`
            }
            data-index={JSON.stringify(item.productIds)}
          />
          {isSainsburys && item?.buttonText && (
            <StyledCTALink data-test='c002-image-cta' kind='primary' full isCarousel>
              {item?.buttonText}
            </StyledCTALink>
          )}
          {!isSainsburys && (
            <StyledViewProductsIcon
              data-test={`c002-view-products-icon-${index}`}
              onClick={() => setSelectedImage({ ...item, index })}
            >
              <StyledTrolleyIcon data-test='trolley-icon' />
              {numberOfProducts}
              <StyledViewProductsLabel data-test='c002-view-products-label'>View Products</StyledViewProductsLabel>
            </StyledViewProductsIcon>
          )}
        </WrapperImage>
        {isSainsburys && (
          <StyledInstagramIcon
            data-test='instagram-icon'
            isSains={isSainsburys}
            onClick={() => setSelectedImage({ ...item, index })}
          />
        )}
        {item?.author && !isSainsburys && (
          <StyledAuthorHandle data-test='c002-author-handle'>{`@${item && item.author}`}</StyledAuthorHandle>
        )}
      </StyledImageContainer>
    )
  }

  const renderImages = () => {
    const imagesArray = []
    imageData
      .slice(0, limit)
      .map((item: ImageDataType, index: number) => imagesArray.push(createImageItem(item, index)))

    return imagesArray
  }

  const handleDrawerCarousel = (currentIndex: number, direction: string) => {
    let nextIndex: number

    if (direction === 'next') {
      nextIndex = currentIndex < imageData.length - 1 ? currentIndex + 1 : 0
    } else {
      nextIndex = currentIndex > 0 ? currentIndex - 1 : imageData.length - 1
    }

    setSelectedImage({ ...imageData[nextIndex], index: nextIndex })
    document.querySelector('[data-test=c002-product-container]').scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <AddToTrolley.Context>
      <AddToTrolley.Modal style={{ zIndex: 100 }} />
      <div data-test='c002-container'>
        {heading && (
          <StyledHeadingContainer>
            <h3 data-test='c002-heading' id='componentheading'>
              {heading}
            </h3>
            {!isSainsburys && <StyledInstagramIcon data-test='instagram-icon' />}
          </StyledHeadingContainer>
        )}
        {bodyCopy && <p data-test='c002-body-copy'>{bodyCopy}</p>}
        <SliderWrapper data-test='c002-slider-container'>
          <Carousel
            data-test='c002-slider-inner'
            numberOfProducts={renderImages().length}
            role='list'
            aria-describedby='componentheading'
          >
            {renderImages()}
          </Carousel>
        </SliderWrapper>
        <DrawerVariant
          heading={heading}
          displayImage={selectedImage}
          toggleCallback={() => {
            setSelectedImage(null)
            onQuickViewSlidingPanelShut(window)
          }}
          carouselCallback={(currentIndex, direction) => handleDrawerCarousel(currentIndex, direction)}
          channel={channel}
        />
      </div>
    </AddToTrolley.Context>
  )
}

export default memo(C002)
